/**
 * KI-LEISTE FUER DEN BETREIBER (Rafael, 03.10.2026 14:09): „was die KI-Accounts anzeigt,
 * möchte ich auch schon einen Balken … oben einfach einmal über die Breite durchziehen …
 * Dass das nur in KI 4 von 9 zusammengefasst wird, ist mir zu kompakt."
 *
 * Reine Rechenfunktionen fuer die Leiste (KiLeiste.tsx): je Zugang Woche und Kurzzeit mit
 * Prozent und kurzer Restzeit bis zur naechsten Freigabe. Unbekannte Messung bleibt null
 * (angezeigt als „?"), nie 0 % — eine 0 saehe aus wie „frei" (siehe UsagePill).
 */
import type { UsageAccount } from './kiSammelStand';
import { KI_GELB_AB, KI_ROT_AB, kiFuellFarbe } from './kiGrenzen';

/** Ab hier bernstein („hoch") — gemeinsame Grenze, lib/kiGrenzen.ts. */
export const KI_LEISTE_HOCH_AB = KI_GELB_AB;
/** Ab hier rot — gemeinsame Grenze, lib/kiGrenzen.ts. */
export const KI_LEISTE_ROT_AB = KI_ROT_AB;

export interface KiFenster {
  /** Kontingent genutzt in Prozent, gerundet; null = keine gueltige Messung. */
  prozent: number | null;
  /** Kurze Restzeit („5T", „1T 12h", „4h", „59m"); leer, wenn unbekannt. */
  rest: string;
  /** Genaue Restzeit fuer den Tooltip („5T 2h", „2h 13m", „57m"); leer, wenn unbekannt. */
  genau: string;
}

export interface KiLeistenEintrag {
  accountId: string;
  woche: KiFenster;
  kurzzeit: KiFenster | null;
  /** Messung als Ganzes ungueltig oder veraltet — alles „?". */
  unbekannt: boolean;
  gesperrt: boolean;
}

/** Restzeit kurz und deutsch („5T", „1T 11h", Rafael 03.10.2026 15:24): ab 2 Tagen nur Tage, darunter Tage+Stunden, dann Stunden, unter 1 h Minuten. */
export function restKurz(ms: number): string {
  if (!Number.isFinite(ms)) return '';
  const min = Math.max(0, Math.floor(ms / 60_000));
  if (min < 60) return `${min}m`;
  const std = Math.floor(min / 60);
  if (std < 24) return `${std}h`;
  const tage = Math.floor(std / 24);
  if (tage >= 2) return `${tage}T`;
  const rest = std % 24;
  return rest ? `${tage}T ${rest}h` : `${tage}T`;
}

/** Restzeit genau fuer den Tooltip (Rafael, 03.10.2026 15:28: „Woche 31 % · noch 5T 2h"). */
export function restGenau(ms: number): string {
  if (!Number.isFinite(ms)) return '';
  const min = Math.max(0, Math.floor(ms / 60_000));
  const std = Math.floor(min / 60), tage = Math.floor(std / 24);
  if (tage > 0) return std % 24 ? `${tage}T ${std % 24}h` : `${tage}T`;
  if (std > 0) return min % 60 ? `${std}h ${min % 60}m` : `${std}h`;
  return `${min}m`;
}

/** Text des Ablesers („resets in 5d 10h", „in 1 Std. 59 Min.") in Millisekunden; null wenn nicht lesbar. */
export function textInMs(text: string): number | null {
  if (!text) return null;
  const d = text.match(/(\d+)\s*d\b/);
  const h = text.match(/(\d+)\s*(?:h\b|Std\.?|hr)/);
  const m = text.match(/(\d+)\s*(?:m\b|Min\.?|min)/);
  if (!d && !h && !m) return null;
  return ((d ? +d[1] * 24 * 60 : 0) + (h ? +h[1] * 60 : 0) + (m ? +m[1] : 0)) * 60_000;
}

/**
 * Restzeit bis zur Freigabe. Bevorzugt den absoluten Zeitpunkt (resetsAtIso); sonst den
 * Ablesertext, zurueckgerechnet um das Alter der Messung — der Text gilt ab dem Ablesen,
 * nicht ab jetzt.
 */
export function restMsBis(iso: string | null | undefined, text: string | undefined, gemessen: string | null | undefined, jetzt: number): number | null {
  if (iso) {
    const t = Date.parse(iso);
    if (Number.isFinite(t)) return t - jetzt;
  }
  const ms = textInMs(text ?? '');
  if (ms === null) return null;
  const g = gemessen ? Date.parse(gemessen) : NaN;
  return Number.isFinite(g) ? ms - (jetzt - g) : ms;
}

export function restBis(iso: string | null | undefined, text: string | undefined, gemessen: string | null | undefined, jetzt: number): string {
  const ms = restMsBis(iso, text, gemessen, jetzt);
  return ms === null ? '' : restKurz(ms);
}

const LEER: KiFenster = { prozent: null, rest: '', genau: '' };
const fensterAus = (p: number | null, ms: number | null): KiFenster =>
  ({ prozent: p, rest: ms === null ? '' : restKurz(ms), genau: ms === null ? '' : restGenau(ms) });

const prozent = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : null);

export function kiLeistenEintraege(accounts: UsageAccount[], health: Record<string, { status: string }> = {}, jetzt = Date.now()): KiLeistenEintrag[] {
  return accounts.map(a => {
    const gesperrt = !!a.cliSperre || health[a.accountId]?.status === 'drift';
    if (a.provider === 'codex') {
      const fenster = a.quotaWindows ?? [];
      const wo = fenster.find(w => w.windowDurationMins === 10080) ?? fenster.find(w => w.windowDurationMins !== 300) ?? null;
      const kz = fenster.find(w => w.windowDurationMins === 300) ?? null;
      const f = (w: typeof wo): KiFenster => w ? fensterAus(prozent(w.usedPercent), w.resetsAt * 1000 - jetzt) : LEER;
      const unbekannt = !!a.quotaError || fenster.length === 0;
      return { accountId: a.accountId, woche: unbekannt ? LEER : f(wo), kurzzeit: kz && !unbekannt ? f(kz) : null, unbekannt, gesperrt };
    }
    const s = a.scraped as (UsageAccount['scraped'] & {
      currentSession: { resetsAtIso?: string | null }; weeklyAllModels: { resetsAtIso?: string | null };
    }) | null;
    const unbekannt = !s || a.status === 'stale';
    const woche: KiFenster = unbekannt ? LEER : fensterAus(prozent(s!.weeklyAllModels?.percent),
      restMsBis(s!.weeklyAllModels?.resetsAtIso, s!.weeklyAllModels?.resetDate, a.scrapedTimestamp, jetzt));
    const kurzzeit: KiFenster = unbekannt ? LEER : fensterAus(prozent(s!.currentSession?.percent),
      restMsBis(s!.currentSession?.resetsAtIso, s!.currentSession?.resetIn, a.scrapedTimestamp, jetzt));
    return { accountId: a.accountId, woche, kurzzeit, unbekannt, gesperrt };
  });
}

/** Farbe je Anteil: gruen, bernstein ab „hoch", rot ab 95 %; unbekannt gedaempft. */
export function kiLeistenFarbe(p: number | null): string {
  return kiFuellFarbe(p);
}

/** Ein Limit ist voll: keine neue Anfrage, bis es wieder frei wird — die Restzeit heisst dann „wieder frei in". */
export const KI_LEISTE_VOLL_AB = 100;

/**
 * Tooltip-Zeile je Limit (Rafael, 03.10.2026 15:28): „Woche 31 % · noch 5T 2h",
 * „Stunden 41 % · noch 57m"; voll: „… · voll, wieder frei in 57m"; ohne Messung „?", nie 0 %.
 */
export function kiTooltipZeile(name: 'Woche' | 'Stunden', f: KiFenster | null): string {
  if (!f) return `${name}: kein solches Limit`;
  if (f.prozent === null) return `${name} ? · keine gültige Messung (unbekannt, nicht 0 %)`;
  const zeit = f.genau ? (f.prozent >= KI_LEISTE_VOLL_AB ? ` · voll, wieder frei in ${f.genau}` : ` · noch ${f.genau}`) : '';
  return `${name} ${f.prozent} %${zeit}`;
}

/**
 * PARTNER SEHEN DIE VIER HAUPTZUGAENGE DES HAUSES (Rafael, 03.10.2026 16:35, Option a:
 * „Alle vier, neutral benannt"). Der Server kuerzt fuer Nicht-Admins schon und ersetzt jede
 * Kennung durch einen neutralen Schluessel (server/lib/verbrauch-sicht.ts): eigene
 * Claude-Zugaenge heissen `ki-1`…`ki-4`, gespiegelte des anderen Hauses `dev-ki-n`/
 * `partner-ki-n`, Codex `cx-n`. Partner bekommen in der Leiste nur die eigenen `ki-n`, in
 * Nummernfolge. Eine echte Kennung (Admin-Antwort) faellt hier bewusst durch — dann ist
 * etwas an der Kuerzung falsch, und die Leiste bleibt leer statt Namen zu zeigen.
 */
const HAUS_ZUGANG = /^ki-([1-9][0-9]*)$/;
export function nurHausZugaenge<T extends { accountId: string; provider?: string }>(accounts: T[]): T[] {
  return accounts
    .filter(a => HAUS_ZUGANG.test(a.accountId) && (a.provider ?? 'claude') === 'claude')
    .sort((a, b) => Number(HAUS_ZUGANG.exec(a.accountId)![1]) - Number(HAUS_ZUGANG.exec(b.accountId)![1]));
}
